import { useMemo } from "react";
import { Link, useParams, useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import type { Brand, PersonaLabFlow } from "@shared/schema";
import { formatDistanceToNow } from "date-fns";
import {
  FlaskConical,
  Plus,
  Trash2,
  ChevronRight,
  CheckCircle2,
  Clock,
  User,
  History,
} from "lucide-react";

const STORAGE_KEY = "personaResearchLabState";
const TOTAL_STEPS = 9;

// Lightweight read of the unsaved working draft persisted by the lab page so
// the dashboard can offer a "resume" entry without importing the whole page.
function readDraft(brandId: string): { completed: number; personaId: string } | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.selectedBrandId !== brandId) return null;
    const statuses = parsed?.status && typeof parsed.status === "object" ? parsed.status : {};
    const completed = Object.values(statuses).filter((s) => s === "complete").length;
    const hasResults = parsed?.results && Object.keys(parsed.results).length > 0;
    if (!completed && !hasResults) return null;
    return { completed, personaId: parsed?.selectedPersonaId || "" };
  } catch {
    return null;
  }
}

export default function PersonaLabDashboardPage() {
  const { brandId } = useParams<{ brandId: string }>();
  const [, navigate] = useLocation();
  const { toast } = useToast();

  const { data: brand } = useQuery<Brand>({
    queryKey: ["/api/brands", brandId],
    enabled: !!brandId,
  });

  const { data: flows, isLoading } = useQuery<PersonaLabFlow[]>({
    queryKey: ["/api/brands", brandId, "persona-lab", "flows"],
    enabled: !!brandId,
  });

  const draft = useMemo(() => (brandId ? readDraft(brandId) : null), [brandId]);

  const deleteFlow = useMutation<void, Error, string>({
    mutationFn: async (id) => {
      await apiRequest("DELETE", `/api/persona-lab/flows/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/brands", brandId, "persona-lab", "flows"] });
      toast({ title: "Flow deleted" });
    },
    onError: (e) => toast({ title: "Delete failed", description: e.message, variant: "destructive" }),
  });

  const startNewFlow = () => {
    // A new flow starts from a blank working draft.
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* non-fatal */ }
    navigate(`/brands/${brandId}/persona-intelligence/flow`);
  };

  const handleDelete = (id: string, title: string) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    deleteFlow.mutate(id);
  };

  return (
    <div className="min-h-full bg-background">
      <div className="max-w-[1100px] mx-auto p-4 md:p-6 space-y-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground flex-shrink-0">
              <FlaskConical className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold tracking-tight" data-testid="text-page-title">Persona Research Lab</h1>
              <p className="text-sm text-muted-foreground" data-testid="text-page-subtitle">
                Saved persona research flows for {brand?.name ?? "this brand"}
              </p>
            </div>
          </div>
          <Button onClick={startNewFlow} data-testid="button-new-flow" className="flex-shrink-0">
            <Plus className="h-4 w-4 mr-1.5" />
            New flow
          </Button>
        </div>

        {/* Resume unsaved draft */}
        {draft && (
          <Card className="border-dashed" data-testid="card-draft">
            <CardContent className="flex items-center justify-between gap-3 p-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted flex-shrink-0">
                  <History className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium">Unsaved working draft</p>
                  <p className="text-xs text-muted-foreground">
                    {draft.completed} of {TOTAL_STEPS} steps complete — not yet saved
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/brands/${brandId}/persona-intelligence/flow`)}
                data-testid="button-resume-draft"
                className="flex-shrink-0"
              >
                Resume
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Saved flows */}
        {isLoading ? (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-[72px] rounded-lg border bg-muted/40 animate-pulse" />
            ))}
          </div>
        ) : !flows || flows.length === 0 ? (
          <Card data-testid="empty-flows">
            <CardContent className="flex flex-col items-center justify-center gap-3 py-14 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-muted">
                <FlaskConical className="h-6 w-6 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm font-medium">No saved research flows yet</p>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Start a new flow, run the modules, then save it to track its progress here.
                </p>
              </div>
              <Button onClick={startNewFlow} data-testid="button-new-flow-empty">
                <Plus className="h-4 w-4 mr-1.5" />
                New flow
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2" data-testid="list-flows">
            {flows.map((flow) => {
              const isFinal = flow.status === "final";
              const step = flow.currentStep ?? 0;
              return (
                <Card
                  key={flow.id}
                  className="group hover-elevate transition-colors"
                  data-testid={`card-flow-${flow.id}`}
                >
                  <CardContent className="flex items-center gap-3 p-4">
                    <Link
                      href={`/brands/${brandId}/persona-intelligence/flow/${flow.id}`}
                      className="flex items-center gap-3 min-w-0 flex-1"
                      data-testid={`link-flow-${flow.id}`}
                    >
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary flex-shrink-0">
                        <FlaskConical className="h-5 w-5" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold truncate" data-testid={`text-flow-title-${flow.id}`}>
                          {flow.title}
                        </p>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                          {flow.personaName && (
                            <span className="flex items-center gap-1 min-w-0">
                              <User className="h-3 w-3 flex-shrink-0" />
                              <span className="truncate">{flow.personaName}</span>
                            </span>
                          )}
                          {flow.updatedAt && (
                            <span className="flex-shrink-0">
                              {formatDistanceToNow(new Date(flow.updatedAt), { addSuffix: true })}
                            </span>
                          )}
                        </div>
                      </div>
                    </Link>

                    {isFinal ? (
                      <Badge
                        className="flex-shrink-0 gap-1 bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 hover:bg-emerald-100"
                        data-testid={`badge-status-${flow.id}`}
                      >
                        <CheckCircle2 className="h-3 w-3" />
                        Final
                      </Badge>
                    ) : (
                      <Badge
                        variant="secondary"
                        className="flex-shrink-0 gap-1"
                        data-testid={`badge-status-${flow.id}`}
                      >
                        <Clock className="h-3 w-3" />
                        Step {Math.min(step + 1, TOTAL_STEPS)} of {TOTAL_STEPS}
                      </Badge>
                    )}

                    <Button
                      variant="ghost"
                      size="icon"
                      className="flex-shrink-0 text-muted-foreground hover:text-destructive"
                      onClick={() => handleDelete(flow.id, flow.title)}
                      disabled={deleteFlow.isPending}
                      data-testid={`button-delete-flow-${flow.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
